import { FormSectionHandler } from '../entity.handler';
import { OpenIdConnectIssuers, UserResponse, UserUpdateRequest, CompanyResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, TextField, EMPTY_GUID } from '../utils';
import { OidcAuthService } from 'app/services/oidc/oidc.service';
import { ModalService } from 'app/shared/components/modal';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    [key: string]: string;
}

export class OidcTagsSectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = 'key';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private oidcAuthService: OidcAuthService,
        private modal: ModalService
    ) {
    }

    static async getIssuers(admin: AdminEditorService, ownerId: string): Promise<OpenIdConnectIssuers> {
        let result: OpenIdConnectIssuers = {};
        try {
            let company: CompanyResponse;
            do {
                company = await admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId)).catch(nullAccessDenied);
                if (company) {
                    ownerId = company.owner.id;
                    result = {
                        ...result,
                        ...company.oidc,
                    };
                }
            } while (company && company.id !== EMPTY_GUID && company.owner.id !== EMPTY_GUID);
        } catch (err) { // don't let errors here prevent the user from loading
            console.error(err);
        }
        return result;
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.OIDC_TAGS.${key}`, values);
    }

    applyUpdate(entity: UserResponse, payload: UserUpdateRequest): Promise<UserResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateUser(entity.id, payload));
    }

    async mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        const result = { ...entity.oidcTags };
        Object.keys(result).forEach(key => {
            result[key] = result[key].replace(/\*/g, '•');
        });
        return result;
    }

    mapEditorToEntity(entity: UserResponse, values: EditorValues): Promise<UserResponse> {

        entity.oidcTags = {
            ...entity.oidcTags,
            ...values,
        };
        Object.keys(entity.oidcTags).forEach(key => {
            if (entity.oidcTags[key].includes('•')) { // if there is a bullet in the item, then it wasn't modified, so ignore the change
                delete entity.oidcTags[key];
            }
        });

        return Promise.resolve(entity);
    }

    async getFields(entity: UserResponse): Promise<FormBuilderField[]> {

        if (entity.emailAddress === this.admin.user.emailAddress) {
            this.description = this.translate('DESC_OWN');
        }

        const issuers = await OidcTagsSectionHandler.getIssuers(this.admin, entity.owner.id);
        const fields = Object.keys(issuers).map(id => {
            const issuer = issuers[id];

            const actions = [{
                id: 'associate',
                name: this.translate('FIELDS.LINK', { issuer: issuer.name }),
                icon: 'link',
                click: async (_action, _field, values, _item) => {
                    try {
                        const userInfo = await this.oidcAuthService.authenticateInPopup(issuer.issuer, issuer.clientId);
                        if (userInfo.email && userInfo.sub) {
                            if (userInfo.email === entity.emailAddress) {
                                values[id] = userInfo.sub;
                            } else {
                                this.modal.error(this.translate('ERRORS.LINK_EMAIL', { issuer: issuer.name }));
                            }
                        } else {
                            this.modal.error(this.translate('ERRORS.LINK_FIELDS', { issuer: issuer.name }));
                        }
                    } catch (err) {
                        console.error(err);
                        this.modal.error(this.translate('ERRORS.LINK_FIELDS', { issuer: issuer.name }));
                    }
                },
            }];

            return {
                ...TextField(id, issuer.name, false, 1, 1024),
                actions: entity.emailAddress === this.admin.user.emailAddress ? actions : undefined, // only show link actions for the current user
            };
        });
        return EntityUtils.prepareBulk(!entity, [], fields);
    }

}
